import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AutomatedEmailsSetting,
  EmailDispatchOutcomeDto,
  EmailLogEntry,
  SendTransactionalEmailRequest,
} from '@trp/community/interfaces';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MailApiClient {
  private readonly httpClient = inject(HttpClient);

  /**
   * Ask the backend to send the transactional email to the current user.
   * Recipient, sender and template id are resolved server-side.
   */
  sendTransactionalEmail(
    request: SendTransactionalEmailRequest
  ): Observable<void> {
    return this.httpClient.post<void>('/mail/transactional', request);
  }

  /**
   * Every logged email, newest first — the admin Email Logs table (DATA-2939).
   * Admin-only endpoint. `recipientEmail` narrows the query server-side (the
   * table's search), so it still finds emails older than the returned window.
   */
  getEmailLogs(recipientEmail?: string): Observable<EmailLogEntry[]> {
    return this.httpClient.get<EmailLogEntry[]>('/mail/logs', {
      params: recipientEmail ? { recipientEmail } : {},
    });
  }

  /**
   * The automated-emails on/off flag + whether the signed-in admin may change
   * it (DATA-2939). Admin-only endpoint.
   */
  getAutomatedEmailsSetting(): Observable<AutomatedEmailsSetting> {
    return this.httpClient.get<AutomatedEmailsSetting>('/mail/settings');
  }

  /** Flip the automated-emails flag. Allowlisted admins only (enforced server-side). */
  setAutomatedEmailsSetting(
    enabled: boolean
  ): Observable<AutomatedEmailsSetting> {
    return this.httpClient.put<AutomatedEmailsSetting>('/mail/settings', {
      enabled,
    });
  }

  /**
   * SendGrid editor URL per email type, for the Template ID links of the
   * Transactional Email Types table (DATA-3029). Admin-only endpoint: the URL
   * carries the template's active version id, which is read from SendGrid.
   */
  getTemplateEditUrls(): Observable<Record<string, string>> {
    return this.httpClient.get<Record<string, string>>(
      '/mail/templates/edit-urls'
    );
  }

  /**
   * Send a test email of one type to myself — the "Test Email" button of the
   * Transactional Email Types table (DATA-3029). Admin-only endpoint; the
   * recipient is resolved server-side from the signed-in admin.
   */
  sendTestEmail(emailType: string): Observable<EmailDispatchOutcomeDto> {
    return this.httpClient.post<EmailDispatchOutcomeDto>(
      `/mail/test/${encodeURIComponent(emailType)}`,
      {}
    );
  }

  // TEMPORARY test hook (mode 2) disabled — targets the dev-only, data-mutating
  // POST /mail/test/trigger-webhook. Kept commented (not removed) for easy re-enable.
  // triggerApprovedViaWebhook(nationalDbId: string): Observable<void> {
  //   return this.httpClient.post<void>('/mail/test/trigger-webhook', {
  //     nationalDbId,
  //   });
  // }
}
